import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, X } from "lucide-react";
import { TravelRequestDetails } from "./TravelRequestDetails";
import { TravelRequest } from "@/lib/request-trip/models/travel-request.model";
import { useI18n } from "@/hooks/useI18n";

interface TravelRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  travelRequest: TravelRequest | null;
  onAccept?: (id: string) => Promise<boolean>;
  onDecline?: (id: string) => Promise<boolean>;
  showActions?: boolean;
}

export const TravelRequestModal = ({
  isOpen,
  onClose,
  travelRequest,
  onAccept,
  onDecline,
  showActions = true
}: TravelRequestModalProps) => {
  const { t } = useI18n();
  
  if (!travelRequest) return null;

  const isNewRequest = (createdAt: string) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffInHours = (now.getTime() - created.getTime()) / (1000 * 60 * 60);
    return diffInHours < 24;
  };

  const handleAccept = async () => {
    if (!onAccept) return;
    const success = await onAccept(travelRequest.id);
    if (success) {
      onClose();
    }
  };

  const handleDecline = async () => {
    if (!onDecline) return;
    const success = await onDecline(travelRequest.id);
    if (success) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <span>
            {t('travelRequest.title')}{" "}
            {travelRequest.destinations.length > 0
              ? travelRequest.destinations.join(", ")
              : t('travelRequest.destinationTBD')}
          </span>
          {isNewRequest(travelRequest.created_at) && (
            <Badge variant="default" className="text-xs px-2 py-1 rounded-full">
              {t('common.new')}
            </Badge>
          )}
        </div>
      }
      size="lg"
      footer={
        showActions ? (
          <>
            <Button variant="outline" onClick={handleDecline}>
              <X className="h-4 w-4" /> {t('travelRequest.decline')}
            </Button>
            <Button variant="default" onClick={handleAccept}>
              <Check className="h-4 w-4" /> {t('travelRequest.accept')}
            </Button>
          </>
        ) : null
      }
    >
      <TravelRequestDetails travelRequest={travelRequest} />
    </Modal>
  );
};
